import { Slot } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';
import { type ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from './cn';

/**
 * Button primitive — design system grammar: one accent (primary) per surface,
 * everything else neutral.
 *
 * - `primary` is the brand electric-blue with accent shadow. One per surface.
 * - `secondary` is neutral filled (surface-2 → surface-3 on hover).
 * - `ghost` is transparent with a hover wash.
 * - `danger` is a destructive ghost.
 * - `fab` is a 36px round primary, used for "send" / "submit" affordances.
 * - `pill` is a 36px round secondary for icon-only menu / close buttons.
 *
 * `asChild` swaps the underlying tag via Radix Slot — enables
 * `<Button asChild><Link to="..." /></Button>` for router links.
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium ring-offset-background transition-colors duration-200 ease-out focus-visible:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98] active:transition-transform active:duration-fast',
  {
    variants: {
      variant: {
        primary:
          'bg-primary text-primary-foreground shadow-accent hover:bg-brand-hover active:bg-brand-press',
        secondary:
          'bg-surface-2 text-fg border border-border hover:bg-surface-3 hover:border-border-strong',
        ghost: 'bg-transparent text-fg-2 hover:bg-surface-2 hover:text-fg',
        danger:
          'bg-transparent text-danger border border-border hover:bg-danger/10 hover:border-danger/40',
        fab: 'bg-primary text-primary-foreground rounded-pill shadow-accent hover:bg-brand-hover',
        pill: 'bg-surface text-fg-2 rounded-pill border border-border hover:bg-surface-2 hover:text-fg',
      },
      size: {
        sm: 'h-8 px-3 text-xs rounded-md',
        md: 'h-9 px-4 text-base rounded-md',
        lg: 'h-11 px-5 text-md rounded-md',
        round: 'h-9 w-9 p-0',
        roundLg: 'h-11 w-11 p-0',
      },
    },
    compoundVariants: [
      { variant: 'fab', size: 'sm', className: 'h-8 w-8 p-0' },
      { variant: 'fab', size: 'md', className: 'h-9 w-9 p-0' },
      { variant: 'fab', size: 'lg', className: 'h-11 w-11 p-0' },
      { variant: 'pill', size: 'sm', className: 'h-8 w-8 p-0' },
      { variant: 'pill', size: 'md', className: 'h-9 w-9 p-0' },
      { variant: 'pill', size: 'lg', className: 'h-11 w-11 p-0' },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, type, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, className }))}
        type={asChild ? undefined : (type ?? 'button')}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
